import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Workflow, Plus, Play, Pause, Trash2, ArrowUp, ArrowDown, Mail, Clock,
  ListPlus, UserCog, Zap, Loader2, X, Users, CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_ICON = { send_email: Mail, wait: Clock, add_to_list: ListPlus, update_contact: UserCog };
const STATUS_META = {
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  paused: { label: 'Paused', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const newStep = (type) => ({ id: `s-${Math.random().toString(36).slice(2, 9)}`, type, config: type === 'wait' ? { value: 1, unit: 'days' } : {} });
const blankAutomation = () => ({ name: '', trigger: { type: 'manual', config: {} }, steps: [], status: 'draft' });

const Automations = () => {
  const [automations, setAutomations] = useState([]);
  const [meta, setMeta] = useState({ triggers: [], step_types: [], lists: [], updatable_fields: [] });
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [draft, setDraft] = useState(blankAutomation());
  const [saving, setSaving] = useState(false);
  const [enrollView, setEnrollView] = useState(null); // automation being viewed
  const [enrollments, setEnrollments] = useState([]);
  const [enrollLoading, setEnrollLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        axios.get(`${API}/automations`),
        axios.get(`${API}/automations/meta`),
      ]);
      setAutomations(a.data.automations || []);
      setMeta(m.data || {});
    } catch { toast.error('Failed to load automations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setDraft(blankAutomation()); setBuilderOpen(true); };
  const openEdit = (a) => {
    setDraft({ id: a.id, name: a.name, trigger: a.trigger || { type: 'manual', config: {} }, steps: a.steps || [], status: a.status });
    setBuilderOpen(true);
  };

  const save = async (activate) => {
    if (!draft.name.trim()) { toast.error('Give your automation a name'); return; }
    if (draft.steps.length === 0) { toast.error('Add at least one step'); return; }
    setSaving(true);
    const body = { ...draft, status: activate ? 'active' : (draft.status === 'active' ? 'active' : 'draft'), base_url: window.location.origin };
    try {
      if (draft.id) await axios.put(`${API}/automations/${draft.id}`, body);
      else await axios.post(`${API}/automations`, body);
      toast.success(draft.id ? 'Automation saved' : 'Automation created');
      setBuilderOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const toggle = async (a) => {
    try { await axios.post(`${API}/automations/${a.id}/toggle`); fetchAll(); }
    catch { toast.error('Failed to toggle'); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? Enrolled contacts will stop.`)) return;
    try { await axios.delete(`${API}/automations/${a.id}`); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const openEnrollments = async (a) => {
    setEnrollView(a);
    setEnrollLoading(true);
    setEnrollments([]);
    try {
      const res = await axios.get(`${API}/automations/${a.id}/enrollments`);
      setEnrollments(res.data.enrollments || []);
    } catch { toast.error('Failed to load enrollments'); }
    finally { setEnrollLoading(false); }
  };

  // --- draft step editing helpers ---
  const setStep = (i, config) => setDraft((d) => ({ ...d, steps: d.steps.map((s, j) => j === i ? { ...s, config } : s) }));
  const addStep = (type) => setDraft((d) => ({ ...d, steps: [...d.steps, newStep(type)] }));
  const delStep = (i) => setDraft((d) => ({ ...d, steps: d.steps.filter((_, j) => j !== i) }));
  const moveStep = (i, dir) => setDraft((d) => {
    const arr = [...d.steps]; const j = i + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...d, steps: arr };
  });

  const triggerMeta = meta.triggers.find((t) => t.type === draft.trigger.type) || {};
  const triggerLabel = (t) => (meta.triggers.find((x) => x.type === t?.type) || {}).label || t?.type;

  return (
    <div className="space-y-6" data-testid="automations-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Workflow className="w-6 h-6 text-violet-600" /> Automations
          </h1>
          <p className="text-slate-400">Build journeys that nurture contacts automatically - a trigger, then a sequence of steps.</p>
        </div>
        <Button className="bg-violet-600 hover:bg-violet-500" onClick={openNew} data-testid="new-automation-btn">
          <Plus className="w-4 h-4 mr-2" /> New automation
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : automations.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-800 rounded-2xl" data-testid="automations-empty">
          <Zap className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No automations yet</p>
          <p className="text-slate-500 text-sm mb-4">Create your first nurture journey - e.g. "When a contact confirms opt-in, send a welcome email."</p>
          <Button className="bg-violet-600 hover:bg-violet-500" onClick={openNew}><Plus className="w-4 h-4 mr-2" /> New automation</Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {automations.map((a) => (
            <div key={a.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid={`automation-card-${a.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-semibold text-white truncate">{a.name}</h3>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${(STATUS_META[a.status] || STATUS_META.draft).cls}`}>
                  {(STATUS_META[a.status] || STATUS_META.draft).label}
                </Badge>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1.5 mb-3">
                <Zap className="w-3.5 h-3.5 text-violet-600" /> {triggerLabel(a.trigger)}
                <span className="text-slate-600">·</span> {(a.steps || []).length} step{(a.steps || []).length === 1 ? '' : 's'}
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400 mb-4">
                <span><b className="text-white">{a.stats?.active || 0}</b> active</span>
                <span><b className="text-white">{a.stats?.completed || 0}</b> completed</span>
                <button onClick={() => openEnrollments(a)} className="ml-auto flex items-center gap-1 text-violet-600 hover:text-violet-800" data-testid={`view-enrollments-${a.id}`}>
                  <Users className="w-3.5 h-3.5" /> View
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="bg-slate-800 border-slate-700 text-slate-200" onClick={() => openEdit(a)} data-testid={`edit-automation-${a.id}`}>Edit</Button>
                <Button size="sm" variant="outline" className="bg-slate-800 border-slate-700 text-slate-200" onClick={() => toggle(a)} data-testid={`toggle-automation-${a.id}`}>
                  {a.status === 'active' ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</> : <><Play className="w-3.5 h-3.5 mr-1" /> Activate</>}
                </Button>
                <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-700 ml-auto" onClick={() => remove(a)} data-testid={`delete-automation-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Builder */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-slate-950 border-slate-800" data-testid="automation-builder">
          <DialogHeader>
            <DialogTitle className="text-white">{draft.id ? 'Edit automation' : 'New automation'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Name</label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. New opt-in welcome series" className="bg-slate-900 border-slate-700 text-white" data-testid="automation-name" />
            </div>

            {/* Trigger */}
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4">
              <div className="flex items-center gap-2 text-violet-600 text-sm font-semibold mb-2"><Zap className="w-4 h-4" /> Trigger - when should this run?</div>
              <Select value={draft.trigger.type} onValueChange={(v) => setDraft({ ...draft, trigger: { type: v, config: {} } })}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="automation-trigger"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {meta.triggers.map((t) => <SelectItem key={t.type} value={t.type}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {triggerMeta.help && <p className="text-xs text-slate-500 mt-2">{triggerMeta.help}</p>}
              {triggerMeta.needs === 'list' && (
                <Select value={draft.trigger.config?.list_id || ''} onValueChange={(v) => setDraft({ ...draft, trigger: { ...draft.trigger, config: { list_id: v } } })}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-2" data-testid="trigger-list"><SelectValue placeholder="Choose a list" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {meta.lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-200">Steps</span>
              </div>
              <div className="space-y-3" data-testid="automation-steps">
                {draft.steps.map((s, i) => {
                  const Icon = STEP_ICON[s.type] || Mail;
                  return (
                    <div key={s.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4" data-testid={`step-${i}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-6 h-6 rounded-full bg-slate-800 text-slate-300 text-xs flex items-center justify-center">{i + 1}</span>
                        <Icon className="w-4 h-4 text-violet-600" />
                        <span className="text-sm font-medium text-white capitalize">{s.type.replace('_', ' ')}</span>
                        <div className="ml-auto flex items-center gap-1">
                          <button onClick={() => moveStep(i, -1)} className="text-slate-500 hover:text-white p-1" data-testid={`step-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                          <button onClick={() => moveStep(i, 1)} className="text-slate-500 hover:text-white p-1" data-testid={`step-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                          <button onClick={() => delStep(i)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`step-del-${i}`}><X className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>

                      {s.type === 'send_email' && (
                        <div className="space-y-2">
                          <Input value={s.config.subject || ''} onChange={(e) => setStep(i, { ...s.config, subject: e.target.value })} placeholder="Subject" className="bg-slate-950 border-slate-700 text-white" data-testid={`step-subject-${i}`} />
                          <Textarea value={s.config.body_html || ''} onChange={(e) => setStep(i, { ...s.config, body_html: e.target.value })} placeholder="Email body… use {{first_name}} to personalise" rows={4} className="bg-slate-950 border-slate-700 text-white" data-testid={`step-body-${i}`} />
                          <p className="text-[11px] text-slate-500">An unsubscribe / preference link is added automatically. Only marketing-permitted contacts are emailed.</p>
                        </div>
                      )}
                      {s.type === 'wait' && (
                        <div className="flex items-center gap-2">
                          <Input type="number" min={1} value={s.config.value || 1} onChange={(e) => setStep(i, { ...s.config, value: parseInt(e.target.value, 10) || 1 })} className="w-24 bg-slate-950 border-slate-700 text-white" data-testid={`step-wait-value-${i}`} />
                          <Select value={s.config.unit || 'days'} onValueChange={(v) => setStep(i, { ...s.config, unit: v })}>
                            <SelectTrigger className="w-32 bg-slate-950 border-slate-700 text-white" data-testid={`step-wait-unit-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800">
                              <SelectItem value="minutes">minutes</SelectItem>
                              <SelectItem value="hours">hours</SelectItem>
                              <SelectItem value="days">days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {s.type === 'add_to_list' && (
                        <Select value={s.config.list_id || ''} onValueChange={(v) => setStep(i, { ...s.config, list_id: v })}>
                          <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid={`step-list-${i}`}><SelectValue placeholder="Choose a list" /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            {meta.lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                      {s.type === 'update_contact' && (
                        <div className="flex items-center gap-2">
                          <Select value={s.config.field || ''} onValueChange={(v) => setStep(i, { ...s.config, field: v })}>
                            <SelectTrigger className="w-48 bg-slate-950 border-slate-700 text-white" data-testid={`step-field-${i}`}><SelectValue placeholder="Field" /></SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800">
                              {meta.updatable_fields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input value={s.config.value || ''} onChange={(e) => setStep(i, { ...s.config, value: e.target.value })} placeholder="Value" className="bg-slate-950 border-slate-700 text-white" data-testid={`step-value-${i}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2 mt-3">
                {meta.step_types.map((st) => {
                  const Icon = STEP_ICON[st.type] || Plus;
                  return (
                    <Button key={st.type} variant="outline" size="sm" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => addStep(st.type)} data-testid={`add-step-${st.type}`}>
                      <Icon className="w-3.5 h-3.5 mr-1.5" /> {st.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save draft
            </Button>
            <Button className="bg-violet-600 hover:bg-violet-500" onClick={() => save(true)} disabled={saving} data-testid="save-activate-btn">
              <Play className="w-4 h-4 mr-2" /> Save &amp; activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enrollments viewer */}
      <Dialog open={!!enrollView} onOpenChange={(o) => { if (!o) setEnrollView(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-slate-950 border-slate-800" data-testid="enrollments-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-600" /> Enrolled contacts - {enrollView?.name}
            </DialogTitle>
          </DialogHeader>
          {enrollLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
          ) : enrollments.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center" data-testid="enrollments-empty">
              No one is enrolled yet. {enrollView?.trigger?.type === 'manual'
                ? 'Enroll contacts from the Contacts page (select rows → Enroll).'
                : 'Contacts will appear here when the trigger fires.'}
            </p>
          ) : (
            <div className="space-y-2" data-testid="enrollments-list">
              {enrollments.map((e) => {
                const last = (e.log || [])[e.log?.length - 1];
                const cls = e.status === 'completed' ? 'text-emerald-600'
                  : e.status === 'active' ? 'text-violet-600'
                  : 'text-slate-500';
                return (
                  <div key={e.id} className="flex items-center justify-between gap-3 bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2.5" data-testid={`enrollment-${e.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{e.contact_name}</p>
                      {e.contact_email && <p className="text-xs text-slate-500 truncate">{e.contact_email}</p>}
                      {last && <p className="text-[11px] text-slate-500 mt-0.5">Last: {last.result}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-xs font-medium capitalize flex items-center gap-1 justify-end ${cls}`}>
                        {e.status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5" />}
                        {e.status}
                      </span>
                      <span className="text-[11px] text-slate-500">step {(e.current_step || 0) + 1}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => setEnrollView(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Automations;
